import { useContext, useCallback, useMemo } from 'react';
import StoreContext from 'stores/context/StoreContext';
import type { ModuleType } from 'models/shop/moduleTypes';

/**
 * Хук для проверки доступности модулей
 * Автоматически реагирует на изменения в MobX store благодаря observer в компонентах
 *
 * @returns Объект с методами для проверки доступности модулей
 */
export const useModuleAccess = (): {
  isModuleEnabled: (moduleType: ModuleType) => boolean;
  areModulesEnabled: (moduleTypes: ModuleType[]) => boolean;
  isAnyModuleEnabled: (moduleTypes: ModuleType[]) => boolean;
  getEnabledModules: () => ModuleType[];
} => {
  const { shopStore } = useContext(StoreContext);

  // Получаем enabledModules для отслеживания изменений
  const enabledModules = shopStore.shop?.settings?.enabledModules;

  /**
   * Проверяет, доступен ли указанный модуль
   *
   * @param moduleType - Тип модуля для проверки
   * @returns true, если модуль доступен
   */
  const isModuleEnabled = useCallback(
    (moduleType: ModuleType): boolean => {
      if (enabledModules == null) {
        return false;
      }

      return enabledModules[moduleType] ?? false;
    },
    [enabledModules]
  );

  /**
   * Проверяет, доступны ли все указанные модули
   *
   * @param moduleTypes - Массив типов модулей для проверки
   * @returns true, если все модули доступны
   */
  const areModulesEnabled = useCallback(
    (moduleTypes: ModuleType[]): boolean =>
      moduleTypes.every((moduleType) => isModuleEnabled(moduleType)),
    [isModuleEnabled]
  );

  /**
   * Проверяет, доступен ли хотя бы один из указанных модулей
   *
   * @param moduleTypes - Массив типов модулей для проверки
   * @returns true, если хотя бы один модуль доступен
   */
  const isAnyModuleEnabled = useCallback(
    (moduleTypes: ModuleType[]): boolean =>
      moduleTypes.some((moduleType) => isModuleEnabled(moduleType)),
    [isModuleEnabled]
  );

  /**
   * Получает все включенные модули
   *
   * @returns Массив включенных модулей
   */
  const getEnabledModules = useCallback((): ModuleType[] => {
    if (enabledModules == null) {
      return [];
    }

    return (Object.keys(enabledModules) as ModuleType[]).filter(
      (moduleType) => enabledModules[moduleType]
    );
  }, [enabledModules]);

  return useMemo(
    () => ({
      isModuleEnabled,
      areModulesEnabled,
      isAnyModuleEnabled,
      getEnabledModules,
    }),
    [isModuleEnabled, areModulesEnabled, isAnyModuleEnabled, getEnabledModules]
  );
};
